import { Stack, Button, Box, CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";
import {
  initialValues,
  validationSchema,
  structure,
} from "templates/products/productsListing/structure";
import FormIndex from "components/Atoms/SharedComponents/Forms/main";
import { useDispatch, useSelector } from "react-redux";
import { GetScrollVendorsRequest } from "services/modules/vendors";
import { GetScrollOccasionTypesRequest } from "services/modules/occasions/occasionsTypes";
import { getScrollProductsOptionTypesRequest } from "services/modules/lookups/productOptionTypes";
import { GetScrollCategoriesRequest, GetScrollSubcategoriesRequest } from "services/modules/lookups/categories";
import { getScrollTagsRequest } from "services/modules/lookups/tags";
import { getScrollBrandsRequest } from "services/modules/lookups/brands";
import { getScrollProductsRequest } from "services/modules/products";
import { useThemePalette } from "common/hooks/theme_palette";

export default function EditProductForm({
  id,
  details,
  refsubmit,
  handleSubmit,
  isEventService = false,
}) {
  const { t } = useTranslation();
  const isStore = localStorage?.getItem("user_type") === "Store";

  const { oliveGreen } = useThemePalette();
  const dispatch = useDispatch();
  const load = useSelector((state) => state.products.load);

  const occassions = useSelector(
    (state) => state.occasionsTypes.filterOccation,
  );
  const occassionsCount = useSelector(
    (state) => state.occasionsTypes.listCount,
  );
  const occassionsLoading = useSelector((state) => state.occasionsTypes.load);

  const vendors = useSelector((state) => state.vendors.filterVendors);
  const vendorsCount = useSelector((state) => state.vendors.listCount);
  const vendorsLoading = useSelector((state) => state.vendors.load);

  const optionTypes = useSelector(
    (state) => state.productsOptionTypes.filterProductsOptionTypes,
  );
  const optionTypesCount = useSelector(
    (state) => state.productsOptionTypes.listCount,
  );
  const optionTypesLoading = useSelector(
    (state) => state.productsOptionTypes.load,
  );

  const categories = useSelector((state) => state.categories.filterCategories);
  const categoriesCount = useSelector((state) => state.categories.listCount);
  const categoriesLoading = useSelector((state) => state.categories.load);

  const subcategories = useSelector(
    (state) => state.categories.filterSubcategories,
  );
  const subcategoriesCount = useSelector(
    (state) => state.categories.subcategoriesCount,
  );

  const tags = useSelector((state) => state.tags.filterTags);
  const tagsCount = useSelector((state) => state.tags.listCount);
  const tagsLoading = useSelector((state) => state.tags.load);

  const brands = useSelector((state) => state.brands.filterBrands);
  const brandsCount = useSelector((state) => state.brands.listCount);
  const brandsLoading = useSelector((state) => state.brands.load);

  const products = useSelector((state) => state.products.filterProducts) || [];
  const productsCount = useSelector((state) => state.products.listCount);
  const productsLoading = useSelector((state) => state.products.load);

  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile,
  );

  const memoizedInitialValues = useMemo(() => {
    if (!id) {
      if (isEventService) return { is_event_service: true };
      return isStore ? { store_id: currentUser?.id } : {};
    }
    return initialValues(details);
  }, [id, isStore, isEventService, currentUser?.id, details]);

  const formik = useFormik({
    initialValues: memoizedInitialValues,
    validationSchema: (_) => validationSchema(t, isEventService),
    onSubmit: async (values) => {
      handleSubmit(values, formik);
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
  });

  useEffect(() => {
    if (formik.values.option_types) {
      handleSubmit && handleSubmit(formik.values, formik, true);
    }
  }, [formik.values.option_types, formik, handleSubmit]);

  const fetchVendors = (filterObj) => {
    if (isStore || isEventService) return;
    const requestData = {
      params: {
        // page_size: 10,
        // page_number: 1,
        hide_details: true,
        skip_pagination: true,
        ...filterObj,
      },
    };
    dispatch(GetScrollVendorsRequest(requestData));
  };

  const fetchOccassions = (filterObj) => {
    const requestData = {
      params: {
        // page_size: 10,
        // page_number: 1,
        hide_details: true,
        skip_pagination: true,
        ...filterObj,
      },
    };
    dispatch(GetScrollOccasionTypesRequest(requestData));
  };

  const fetchOptionTypes = (filterObj) => {
    const requestData = {
      params: {
        // page_size: 10,
        // page_number: 1,
        hide_details: true,
        skip_pagination: true,
        ...filterObj,
      },
    };
    dispatch(getScrollProductsOptionTypesRequest(requestData));
  };

  const fetchCategories = (filterObj) => {
    const requestData = {
      params: {
        // page_size: 10,
        // page_number: 1,
        hide_details: true,
        skip_pagination: true,
        ...filterObj,
      },
    };
    dispatch(GetScrollCategoriesRequest(requestData));
  };

  const fetchSubcategories = (filterObj = {}) => {
    const parentIds =
      filterObj?.parent_ids ||
      formik?.values?.categories?.map((item) => item?.value)?.filter(Boolean) ||
      [];
    dispatch(
      GetScrollSubcategoriesRequest({
        params: {
          hide_details: true,
          skip_pagination: true,
          ...filterObj,
          parent_ids: parentIds,
        },
      }),
    );
  };

  const selectedCategoryIds = (formik?.values?.categories || [])
    .map((item) => item?.value)
    .filter(Boolean);
  const selectedCategoryIdsKey = selectedCategoryIds.join(",");

  useEffect(() => {
    if (!selectedCategoryIdsKey) return;
    fetchSubcategories({
      parent_ids: selectedCategoryIdsKey.split(",").map((id) => Number(id) || id),
    });
    // Fetch whenever selected parent categories change (including edit prefills)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryIdsKey, id, details?.id]);

  const fetchTags = (filterObj) => {
    const requestData = {
      params: {
        // page_size: 10,
        // page_number: 1,
        hide_details: true,
        skip_pagination: true,
        ...filterObj,
      },
    };
    dispatch(getScrollTagsRequest(requestData));
  };

  const fetchBrands = (filterObj) => {
    const requestData = {
      params: {
        hide_details: true,
        skip_pagination: true,
        is_active: true,
        ...filterObj,
      },
    };
    dispatch(getScrollBrandsRequest(requestData));
  };

  const fetchProducts = (filterObj) => {
    dispatch(
      getScrollProductsRequest({
        params: {
          // page_size: 10,
          // page_number: 1,
          skip_pagination: true,
          ...filterObj,
        },
      }),
    );
  };

  const prepareVendorsOptions = () => {
    if (isStore) {
      //to display only the logged in store in the vendors DDL when adding a product
      return [details?.store];
    } else {
      return [details?.store, ...vendors];
    }
  };

  const prepareBrandsOptions = () => {
    const merged = [details?.brand, ...(brands || [])].filter(Boolean);
    const seen = new Set();
    return merged.filter((brand) => {
      if (!brand?.id || seen.has(brand.id)) return false;
      seen.add(brand.id);
      return true;
    });
  };

  const options = {
    vendors: {
      options: prepareVendorsOptions(),
      onOpen: vendors?.length <= 1 && fetchVendors,
      isLoading: vendorsLoading?.loading,
      getList: fetchVendors,
      applySearchFromRequest: true,
      count: vendorsCount,
    },
    brands: {
      options: prepareBrandsOptions(),
      onOpen: brands?.length <= 1 && fetchBrands,
      isLoading:
        brandsLoading?.loading &&
        brandsLoading?.type === "filter-brands-list",
      getList: fetchBrands,
      applySearchFromRequest: true,
      count: brandsCount,
    },
    occassions: {
      options: occassions,
      onOpen: occassions?.length <= 1 && fetchOccassions,
      isLoading: occassionsLoading?.loading,
      getList: fetchOccassions,
      applySearchFromRequest: true,
      count: occassionsCount,
    },
    optionTypes: {
      options: optionTypes,
      onOpen: optionTypes?.length <= 1 && fetchOptionTypes,
      isLoading: optionTypesLoading?.loading,
      getList: fetchOptionTypes,
      applySearchFromRequest: true,
      count: optionTypesCount,
    },
    categories: {
      options: categories,
      onOpen: categories?.length <= 1 && fetchCategories,
      isLoading: categoriesLoading?.loading,
      getList: fetchCategories,
      applySearchFromRequest: true,
      count: categoriesCount,
    },
    subcategories: {
      options: [
        ...(formik?.values?.subcategories || []).map((item) => ({
          id: item?.value,
          name: item?.label,
        })),
        ...(subcategories || []),
      ],
      onOpen: () => {
        if (formik?.values?.categories?.length > 0) {
          fetchSubcategories({
            parent_ids: formik.values.categories
              .map((item) => item?.value)
              .filter(Boolean),
          });
        }
      },
      isLoading:
        categoriesLoading?.loading &&
        categoriesLoading?.type === "subcategories-list",
      getList: (filterObj) =>
        fetchSubcategories({
          ...filterObj,
          parent_ids: formik?.values?.categories?.map((item) => item?.value),
        }),
      applySearchFromRequest: true,
      count: subcategoriesCount,
    },
    onCategoriesChange: (value) => {
      const parentIds = (value || [])
        .map((item) => item?.value)
        .filter(Boolean);
      fetchSubcategories({ parent_ids: parentIds });
    },
    tags: {
      options: tags,
      onOpen: tags?.length <= 1 && fetchTags,
      isLoading: tagsLoading?.loading,
      getList: fetchTags,
      applySearchFromRequest: true,
      count: tagsCount,
    },
    recommendations: {
      options: products,
      onOpen: products?.length <= 1 && fetchProducts,
      isLoading:
        productsLoading?.loading &&
        productsLoading?.type === "filter-products-list",
      getList: fetchProducts,
      applySearchFromRequest: true,
      count: productsCount,
    },
    recommendedCategories: {
      options: categories,
      onOpen: categories?.length <= 1 && fetchCategories,
      isLoading: categoriesLoading?.loading,
      getList: fetchCategories,
      applySearchFromRequest: true,
      count: categoriesCount,
    },
  };
  // console.log(options?.categories);
  return (
    <Stack sx={{ overflowY: "auto", height: { xs: "auto", md: 630 }, p: 2 }}>
      {((load?.loading && load?.type === "details") ||
        (load?.loading && load?.type === "update")) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bottom: "0",
            left: "0",
            right: 0,
            top: 0,
            position: "absolute",
            zIndex: 1000,
            bgcolor: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(0.6px)",
          }}
        >
          <CircularProgress sx={{ color: oliveGreen }} />
        </Box>
      )}
      <FormIndex
        formStructures={structure(
          options,
          isStore,
          t,
          formik.values,
          isEventService,
        )}
        formik={formik}
        refsubmit={refsubmit}
      >
        <Button ref={refsubmit} type="submit"></Button>
      </FormIndex>
    </Stack>
  );
}
